import { Component, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Caractéristique } from '../models/caractéristique.model';
import { CaracteristiqueUpdateRequest } from '../requests/caractéristique-update-request';
import { CaractéristiqueStore } from '../store/caractéristique.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-caracteristique',
  imports: [ReactiveFormsModule, HlmFieldImports, HlmInputImports, LabelRequiredDirective],
  templateUrl: './form-caracteristique.component.html',
})
export class FormCaracteristiqueComponent {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #caractéristiqueStore = inject(CaractéristiqueStore);

  readonly referentiel = input.required<Référentiel>();
  readonly caracteristique = input.required<Caractéristique>();

  readonly form = this.#fb.nonNullable.group({
    nom: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      const caractéristique = this.caracteristique();

      this.nom.setValue(caractéristique.nom);
    });

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }

    const toastrId = uuidv7();

    const caractéristique: CaracteristiqueUpdateRequest = {
      nom: this.nom.value,
    };

    this.#caractéristiqueStore.modifier({
      idCaracteristique: this.caracteristique().id,
      data: caractéristique,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Modification de la caractéristique réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#caractéristiqueStore.charger(this.referentiel().id);
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(caractéristique),
    });
  }

  alreadyExists(caractéristique: { nom: string }) {
    const caractéristiques = this.#caractéristiqueStore.entities().map((x) => x.nom);
    const doublonsAvecCaractéristiquesExistantes = caractéristiques.filter((x: string) => caractéristique.nom === x);

    if (doublonsAvecCaractéristiquesExistantes.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }
}
